/**
 * Tablet Registration V2
 * Bold, left-aligned kiosk layout for AvePark.
 * Steps: Welcome → Account Type → Form+Consents → (Add Person) → (Print Declarations) → Thank You
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, Eye, EyeOff, Loader2, Check, ArrowLeft, ArrowRight,
  User, Users, AlertCircle, CheckCircle, X, AlertTriangle,
  Mail, Phone, Calendar, Lock, UserCircle, ServerOff, Printer
} from 'lucide-react';
import { useTranslations } from '../context/TranslationsContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PhonePrefixSelect from '../components/PhonePrefixSelect';
import Logo from '../components/Logo';
import FlagIcon from '../components/FlagIcon';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import { printDeclarations, prefetchDeclarationTexts } from '../utils/declarationPdf';

const API = process.env.REACT_APP_BACKEND_URL;
const INACTIVITY_TIMEOUT = 5 * 60 * 1000;
const WARNING_DURATION = 10 * 1000;

const calculateAge = (birthDate) => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

// Stagger animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
};

const slideUpVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } }
};

// Minor Alert Component
const MinorAlert = ({ t }) => (
  <motion.div variants={itemVariants}
    className="mt-2 p-5 rounded-2xl border-2 border-amber-500/50 bg-amber-500/10"
  >
    <div className="flex items-start gap-3">
      <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <h4 className="font-bold text-amber-500 text-sm uppercase tracking-wider">
          {t('auth.minorAlert.title', 'Osoba niepełnoletnia')}
        </h4>
        <p className="text-sm text-white/60 mt-1">
          {t('auth.minorAlert.description', 'Jeśli jesteś niepełnoletni, rodzic/opiekun musi wypełnić oświadczenie.')}
        </p>
        <p className="text-sm font-semibold text-white/70 mt-2">
          {t('tablet.minorAlert.autoPrintInfo', 'Oświadczenie zostanie automatycznie wydrukowane w kolejnym kroku. Poproś rodzica/opiekuna o jego wypełnienie i podpisanie.')}
        </p>
      </div>
    </div>
  </motion.div>
);

// Percentage progress bar
const ProgressBar = ({ percent }) => (
  <div className="h-1.5 w-full bg-white/[0.08] rounded-full overflow-hidden mb-2">
    <div className="h-full rounded-full bg-[#7C3AED] transition-all duration-500" style={{ width: `${percent}%` }} />
  </div>
);

// Field label component — MUST be outside main component to avoid re-mount on state change
const Field = ({ icon: Icon, label, children }) => (
  <div>
    <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-white/40 mb-2.5">
      {Icon && <Icon className="w-4 h-4" />}
      {label}
    </label>
    {children}
  </div>
);

const TabletRegistrationV2 = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { td } = useTranslations();

  // Local fitnet status — kiosk-resilient
  const [serviceStatus, setServiceStatus] = useState('checking');
  const consecutiveFailsRef = useRef(0);
  const wasEverAvailableRef = useRef(false);

  useEffect(() => {
    const checkService = async () => {
      try {
        const res = await axios.get(`${API}/api/fitnet-status`, { timeout: 10000 });
        if (res.data?.available) {
          consecutiveFailsRef.current = 0;
          wasEverAvailableRef.current = true;
          setServiceStatus('available');
        } else {
          consecutiveFailsRef.current++;
          if (!wasEverAvailableRef.current || consecutiveFailsRef.current >= 2) setServiceStatus('unavailable');
        }
      } catch {
        consecutiveFailsRef.current++;
        if (!wasEverAvailableRef.current || consecutiveFailsRef.current >= 2) setServiceStatus('unavailable');
      }
    };
    checkService();
    const interval = setInterval(checkService, 30000);
    return () => clearInterval(interval);
  }, []);

  // Pre-warm the declaration translation cache on mount so print has zero network latency.
  useEffect(() => { prefetchDeclarationTexts(); }, []);

  // Steps: 0=Welcome, 1=Account type, 2=Form+Consents, 3=Add person, 4=Thank you
  const [step, setStep] = useState(0);
  const [accountType, setAccountType] = useState(null);

  const [languages, setLanguages] = useState([]);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', phoneCode: '+48',
    birthDate: '', password: '', confirmPassword: '', gender: '',
    agreementTermsOfService: false, agreementSkateparkRules: false,
    agreementPrivacyPolicy: false, agreementMarketing: false, agreementNewsletter: false
  });

  const [subaccountData, setSubaccountData] = useState({
    firstName: '', lastName: '', birthDate: '', gender: '', phone: '', phoneCode: '+48'
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdUser, setCreatedUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [addedSubaccounts, setAddedSubaccounts] = useState([]);

  const [documentModal, setDocumentModal] = useState({ open: false, slug: '', title: '' });
  const [documentContent, setDocumentContent] = useState('');
  const [documentLoading, setDocumentLoading] = useState(false);

  // Print declarations state
  const [presenceChoices, setPresenceChoices] = useState({}); // { "childName": "without" | "with" }
  const [isPrinting, setIsPrinting] = useState(false);
  const [printDone, setPrintDone] = useState(false);

  const [showWarning, setShowWarning] = useState(false);
  const [warningProgress, setWarningProgress] = useState(100);
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const warningIntervalRef = useRef(null);

  const lang = i18n.language;

  const t = useCallback((key, fallback = null) => {
    const tabletKey = `tablet.${key}`;
    const tabletValue = td(tabletKey);
    if (tabletValue && tabletValue !== tabletKey) return tabletValue;
    const cmsValue = td(key);
    if (cmsValue && cmsValue !== key) return cmsValue;
    const i18nVal = ti18n(key);
    if (i18nVal && i18nVal !== key) return i18nVal;
    return fallback || key;
  }, [td, ti18n]);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get(`${API}/api/languages`);
        if (response.data?.length > 0) setLanguages(response.data);
      } catch {
        setLanguages([{ code: 'pl', nativeName: 'Polski' }, { code: 'en', nativeName: 'English' }]);
      }
    };
    fetchLanguages();
    const savedLang = localStorage.getItem('i18nextLng');
    if (!savedLang || savedLang.length !== 2) i18n.changeLanguage('pl');
  }, [i18n]);

  const passwordStrength = useMemo(() => {
    const pw = formData.password;
    if (!pw) return { score: 0, label: '', color: '' };
    const checks = { min: pw.length >= 8, up: /[A-Z]/.test(pw), lo: /[a-z]/.test(pw), num: /[0-9]/.test(pw), sp: /[^A-Za-z0-9]/.test(pw) };
    const score = Object.values(checks).filter(Boolean).length;
    if (score === 5) return { score, label: t('auth.passwordStrong', 'Silne'), color: 'bg-green-500' };
    if (score >= 4) return { score, label: t('auth.passwordGood', 'Dobre'), color: 'bg-blue-500' };
    if (score >= 3) return { score, label: t('auth.passwordMedium', 'Średnie'), color: 'bg-yellow-500' };
    return { score, label: t('auth.passwordWeak', 'Słabe'), color: 'bg-red-500' };
  }, [formData.password, t]);

  const resetToWelcome = useCallback(() => {
    setStep(0);
    setAccountType(null);
    setFormData({
      firstName: '', lastName: '', email: '', phone: '', phoneCode: '+48',
      birthDate: '', password: '', confirmPassword: '', gender: '',
      agreementTermsOfService: false, agreementSkateparkRules: false,
      agreementPrivacyPolicy: false, agreementMarketing: false, agreementNewsletter: false
    });
    setSubaccountData({ firstName: '', lastName: '', birthDate: '', gender: '', phone: '', phoneCode: '+48' });
    setError(''); setCreatedUser(null); setAuthToken(null); setAddedSubaccounts([]);
    setShowWarning(false); setWarningProgress(100);
    setPresenceChoices({}); setIsPrinting(false); setPrintDone(false);
  }, []);

  // Inactivity
  const stepRef = useRef(step);
  const showWarningRef = useRef(showWarning);
  useEffect(() => { stepRef.current = step; }, [step]);
  useEffect(() => { showWarningRef.current = showWarning; }, [showWarning]);

  const handleActivity = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (warningIntervalRef.current) clearInterval(warningIntervalRef.current);
    if (showWarningRef.current) { setShowWarning(false); setWarningProgress(100); }
    if (stepRef.current === 0) return;
    inactivityTimerRef.current = setTimeout(() => {
      setShowWarning(true); setWarningProgress(100);
      const startTime = Date.now();
      warningIntervalRef.current = setInterval(() => {
        const remaining = Math.max(0, 100 - ((Date.now() - startTime) / WARNING_DURATION) * 100);
        setWarningProgress(remaining);
        if (remaining <= 0) clearInterval(warningIntervalRef.current);
      }, 50);
      warningTimerRef.current = setTimeout(() => resetToWelcome(), WARNING_DURATION);
    }, INACTIVITY_TIMEOUT);
  }, [resetToWelcome]);

  useEffect(() => {
    const events = ['mousedown', 'touchstart', 'keydown'];
    events.forEach(e => document.addEventListener(e, handleActivity));
    return () => {
      events.forEach(e => document.removeEventListener(e, handleActivity));
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (warningIntervalRef.current) clearInterval(warningIntervalRef.current);
    };
  }, [handleActivity]);

  useEffect(() => { if (step !== 0) handleActivity(); }, [step, handleActivity]);
  useEffect(() => { if (step === 5) { const t2 = setTimeout(resetToWelcome, 30000); return () => clearTimeout(t2); } }, [step, resetToWelcome]);

  const changeLanguage = (langCode) => { i18n.changeLanguage(langCode); setLangMenuOpen(false); handleActivity(); };
  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const translateError = (errorMsg) => {
    const m = {
      'Missing required fields': t('errors.missingFields', 'Wypełnij wszystkie wymagane pola'),
      'Required consents must be accepted': t('errors.consentsRequired', 'Zaakceptuj wymagane zgody'),
      'Email is already registered': t('errors.emailExists', 'Ten email jest już zarejestrowany'),
      'User already exists': t('errors.emailExists', 'Ten email jest już zarejestrowany'),
      'Registration failed': t('errors.registrationFailed', 'Rejestracja nie powiodła się'),
      'Failed to create subaccount': t('errors.subaccountFailed', 'Nie udało się dodać osoby'),
    };
    return m[errorMsg] || errorMsg;
  };

  const openDocumentModal = async (slug, title) => {
    setDocumentModal({ open: true, slug, title }); setDocumentLoading(true); setDocumentContent('');
    try {
      const response = await axios.get(`${API}/api/documents/${slug}?lang=${lang}`);
      const content = response.data.content;
      if (typeof content === 'object' && content !== null) setDocumentContent(content[lang] || content.pl || Object.values(content)[0] || '');
      else setDocumentContent(content || '');
    } catch { setDocumentContent(t('errors.documentLoadFailed', 'Nie udało się załadować dokumentu')); }
    finally { setDocumentLoading(false); }
  };
  const closeDocumentModal = () => { setDocumentModal({ open: false, slug: '', title: '' }); setDocumentContent(''); };

  // Submit
  const handleRegister = async () => {
    setError('');
    if (!formData.agreementTermsOfService || !formData.agreementSkateparkRules || !formData.agreementPrivacyPolicy) {
      setError(t('auth.acceptAllRequired', 'Zaakceptuj wszystkie wymagane zgody')); return;
    }
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError(t('auth.fillRequiredFields', 'Wypełnij wszystkie wymagane pola')); return;
    }
    if (!formData.gender) { setError(t('auth.selectGender', 'Wybierz płeć')); return; }
    if (!formData.birthDate) { setError(t('auth.birthDateRequired', 'Data urodzenia jest wymagana')); return; }

    setLoading(true);
    try {
      const payload = { ...formData, sex: formData.gender, language: lang };
      delete payload.confirmPassword; delete payload.gender;
      const response = await axios.post(`${API}/api/auth/tablet-register`, payload, { timeout: 60000 });
      if (response.data.user) {
        setCreatedUser(response.data.user); setAuthToken(response.data.token);
        goAfterRegister();
      }
    } catch (err) {
      if (err.code === 'ECONNABORTED' || !err.response) {
        try {
          const loginRes = await axios.post(`${API}/api/auth/login`, { email: formData.email, password: formData.password });
          if (loginRes.data.token) { setCreatedUser(loginRes.data.user); setAuthToken(loginRes.data.token); goAfterRegister(); return; }
        } catch { }
        setError(t('auth.registrationSlow', 'Rejestracja trwa dłużej niż zwykle. Spróbuj ponownie za chwilę.'));
      } else { setError(translateError(err.response?.data?.error || 'Registration failed')); }
    } finally { setLoading(false); }
  };

  const handleAddSubaccount = async () => {
    setError('');
    if (!subaccountData.firstName.trim() || !subaccountData.lastName.trim() || !subaccountData.birthDate) { setError(t('auth.fillPersonData', 'Wypełnij imię, nazwisko i datę urodzenia')); return; }
    if (!subaccountData.gender) { setError(t('auth.selectGender', 'Wybierz płeć')); return; }
    if (!subaccountData.phone.trim()) { setError(t('auth.phoneRequired', 'Numer telefonu jest wymagany')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/subaccounts`, { ...subaccountData }, { headers: { Authorization: `Bearer ${authToken}` }, timeout: 30000 });
      setAddedSubaccounts(prev => [...prev, { firstName: subaccountData.firstName, lastName: subaccountData.lastName, birthDate: subaccountData.birthDate }]);
      setSubaccountData({ firstName: '', lastName: '', birthDate: '', gender: '', phone: '', phoneCode: '+48' });
    } catch (err) {
      if (err.code === 'ECONNABORTED' || !err.response) {
        setAddedSubaccounts(prev => [...prev, { firstName: subaccountData.firstName, lastName: subaccountData.lastName, birthDate: subaccountData.birthDate }]);
        setSubaccountData({ firstName: '', lastName: '', birthDate: '', gender: '', phone: '', phoneCode: '+48' });
      } else { setError(translateError(err.response?.data?.error || 'Failed to create subaccount')); }
    } finally { setLoading(false); }
  };

  // Collect all minors from form + subaccounts
  // For family accounts: main user = parent, subaccounts may be minors
  // For solo accounts: if main user is minor, they are the child (no parent data available)
  const isMainUserMinor = formData.birthDate && calculateAge(formData.birthDate) < 18;

  const getAllMinors = useCallback(() => {
    const minors = [];
    // For solo: main user themselves
    if (isMainUserMinor) {
      minors.push({ firstName: formData.firstName, lastName: formData.lastName, birthDate: formData.birthDate, isSelf: true });
    }
    // For family: subaccounts
    addedSubaccounts.forEach(sub => {
      if (sub.birthDate && calculateAge(sub.birthDate) < 18) {
        minors.push({ firstName: sub.firstName, lastName: sub.lastName, birthDate: sub.birthDate, isSelf: false });
      }
    });
    return minors;
  }, [formData, addedSubaccounts, isMainUserMinor]);

  // Steps: 0=Welcome, 1=Type, 2=Form, 3=AddPerson(family), 4=PrintDeclarations(if minors), 5=ThankYou
  const goToFinish = useCallback(() => {
    const minors = getAllMinors();
    if (minors.length > 0) {
      // Initialize presence choices for each minor
      const choices = {};
      minors.forEach(m => { choices[`${m.firstName} ${m.lastName}`] = ''; });
      setPresenceChoices(choices);
      setPrintDone(false);
      setStep(4); // Print declarations
    } else {
      setStep(5); // Thank you
    }
  }, [getAllMinors]);

  const goAfterRegister = useCallback(() => {
    if (accountType === 'family') {
      setStep(3); // Add person
    } else {
      // Solo — check if main user is minor
      if (formData.birthDate && calculateAge(formData.birthDate) < 18) {
        // Solo minor: no presence choice needed, parent fills that
        setPresenceChoices({});
        setPrintDone(false);
        setStep(4);
      } else {
        setStep(5);
      }
    }
  }, [accountType, formData]);

  const currentLang = languages.find(l => l.code === i18n.language) || { code: 'pl', nativeName: 'Polski' };

  const inputClass = "w-full px-4 rounded-2xl text-white bg-white/[0.06] border border-white/[0.12] focus:border-[#7C3AED] focus:outline-none transition-colors placeholder:text-white/25"
    + " text-base";

  // =================== SHARED UI ===================

  const renderLangSelector = () => (
    <div className="relative z-50">
      <button onClick={() => setLangMenuOpen(!langMenuOpen)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-md rounded-full text-white hover:bg-white/20 transition-colors"
        data-testid="tabletv2-lang-selector">
        <FlagIcon code={currentLang.code} size={22} />
        <span className="font-semibold text-sm uppercase tracking-wider">{currentLang.code}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {langMenuOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 mt-2 bg-black/90 backdrop-blur-xl rounded-2xl p-2 min-w-[160px] border border-white/10">
            {languages.map(l => (
              <button key={l.code} onClick={() => changeLanguage(l.code)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${l.code === i18n.language ? 'bg-[#7C3AED]/30 text-white' : 'text-white/80 hover:bg-white/10'}`}>
                <FlagIcon code={l.code} size={20} />
                <span>{l.nativeName}</span>
                {l.code === i18n.language && <Check className="w-4 h-4 ml-auto" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const renderHeader = (backAction, showLang = true) => (
    <div className="flex items-center justify-between px-10 pt-6 pb-3">
      {backAction ? (
        <button onClick={backAction}
          className="flex items-center gap-2 px-6 py-3.5 bg-white/10 hover:bg-white/15 rounded-full text-white transition-colors"
          data-testid="tabletv2-back-btn">
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-bold uppercase tracking-wider">{t('common.back', 'Wstecz')}</span>
        </button>
      ) : <div />}
      {showLang ? renderLangSelector() : <div />}
    </div>
  );

  const renderDocumentModal = () => (
    <AnimatePresence>
      {documentModal.open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-6" onClick={closeDocumentModal}>
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
            className="bg-[#111] border border-white/10 rounded-3xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold">{documentModal.title}</h2>
              <button onClick={closeDocumentModal} className="p-2 hover:bg-white/10 rounded-full"><X className="w-6 h-6" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {documentLoading ? <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" /></div>
                : <div className="prose prose-invert prose-sm max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{documentContent}</ReactMarkdown></div>}
            </div>
            <div className="p-6 border-t border-white/10">
              <button onClick={closeDocumentModal} className="w-full h-14 text-white font-bold rounded-full bg-[#7C3AED] text-lg">{t('common.close', 'Zamknij')}</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const renderWarning = () => (
    <AnimatePresence>
      {showWarning && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-8" onClick={handleActivity}>
          <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
            className="bg-[#111] border border-white/10 rounded-3xl p-12 max-w-lg text-center" onClick={e => e.stopPropagation()}>
            <AlertCircle className="w-20 h-20 text-yellow-500 mx-auto mb-6" />
            <h2 className="text-3xl font-black uppercase mb-4" style={{ fontFamily: "'Syne', sans-serif" }}>{t('tabletv2.sessionExpiring', 'Sesja wygasa')}</h2>
            <p className="text-xl text-white/60 mb-8">{t('tabletv2.touchToContinue', 'Dotknij ekranu, aby kontynuować')}</p>
            <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-yellow-500 to-red-500 transition-all" style={{ width: `${warningProgress}%` }} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Keyboard detection not needed — all sizes use viewport-relative clamp()

  // =================== STEPS ===================

  // Step 0: Welcome
  const renderWelcome = () => (
    <div className="relative h-screen flex flex-col overflow-hidden" data-testid="tabletv2-welcome">
      <div className="absolute inset-0 z-0">
        <video autoPlay muted loop playsInline className="w-full h-full object-cover">
          <source src="/videos/tablet-video.webm" type="video/webm" />
          <source src="/videos/tablet-video.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/80" />
      </div>

      {/* Top: just logo */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="relative z-10 px-10 pt-10">
        <Logo className="h-20" variant="light" />
      </motion.div>

      <div className="absolute top-7 right-7 z-50">
        {renderLangSelector()}
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col justify-center px-10">
        <motion.p initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
          className="text-lg font-bold uppercase tracking-[0.3em] text-[#7C3AED] mb-4 flex items-center gap-3">
          <span className="w-10 h-[2px] bg-[#7C3AED]" />
          {t('tablet.registration', 'REJESTRACJA')}
        </motion.p>

        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.6 }}
          className="text-[6.5rem] leading-[0.9] font-black uppercase mb-8" style={{ fontFamily: "'Syne', sans-serif" }}>
          <span className="text-white">{t('welcomeTo', 'Witaj w')}</span>
          <br />
          <span className="text-[#7C3AED]">AvePark</span>
        </motion.h1>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
          className="text-2xl text-white/50 max-w-lg leading-relaxed">
          {t('tablet.welcomeSubtitle', 'Dotknij ekranu, aby założyć konto i zacząć jeździć z nami — BMX, hulajnoga, deskorolka, rolki.')}
        </motion.p>
      </div>

      {/* CTA */}
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.5 }}
        className="relative z-10 px-10 pb-14">
        <button onClick={() => setStep(1)}
          className="w-full py-8 text-3xl font-black uppercase tracking-wider rounded-3xl text-white flex items-center justify-center gap-4 bg-[#7C3AED] hover:bg-[#6D28D9] transition-all active:scale-[0.98]"
          data-testid="tabletv2-start-btn">
          {t('tablet.startRegistration', 'Zarejestruj się')}
          <ArrowRight className="w-8 h-8" />
        </button>
        <p className="text-center text-xs uppercase tracking-[0.25em] text-white/25 mt-8">
          ← {t('tabletv2.touchToStart', 'dotknij przycisk, aby rozpocząć')} →
        </p>
      </motion.div>
    </div>
  );

  // Step 1: Account type
  const renderAccountType = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
      className="h-screen flex flex-col overflow-hidden" style={{ background: '#050505' }} data-testid="tabletv2-account-type">
      {renderHeader(resetToWelcome)}
      <div className="px-10 pt-1">
        <ProgressBar percent={0} />
      </div>

      <div className="flex-1 px-10 pt-2 flex flex-col overflow-hidden min-h-0">
        <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#7C3AED] mb-2 flex items-center gap-3">
          <span className="w-10 h-[2px] bg-[#7C3AED]" />
          {t('tabletv2.step1', 'Krok 1')}
        </p>
         <h1 className="font-black uppercase leading-[0.9] mb-2" style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(1.8rem, 3.5vh, 3rem)' }}>
          <span className="text-white">{t('tabletv2.chooseAccountType', 'Wybierz')}</span>{' '}<br />
          <span className="text-[#7C3AED]">{t('accountTypeLabel', 'typ konta')}</span>
        </h1>
        <p className="text-sm text-white/40 mb-8 max-w-lg">
          {t('tabletv2.accountTypeDescription', 'Zarejestruj konto wyłącznie dla siebie lub dołącz do niego dane swoich dzieci.')}
        </p>

        <div className="flex flex-col gap-3 flex-1 min-h-0">
          {[
            { type: 'solo', icon: User, badge: t('tabletv2.onePerson', 'Jedna osoba'), title: t('tabletv2.soloTitle', 'Tylko ja'), desc: t('tabletv2.soloDesc', 'Konto indywidualne — rejestracja wyłącznie dla Ciebie') },
            { type: 'family', icon: Users, badge: t('tabletv2.manyPeople', 'Wiele osób'), title: t('tabletv2.familyTitle', 'Ja i dzieci'), desc: t('tabletv2.familyDesc', 'Konto rodzinne — dodaj dane swoich dzieci po rejestracji') }
          ].map((opt, i) => (
            <motion.button key={opt.type}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + i * 0.1, duration: 0.35 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => { setAccountType(opt.type); setStep(2); }}
              className="relative flex flex-col justify-end p-6 rounded-3xl border-2 text-left active:scale-[0.97] transition-transform flex-1 min-h-0 overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, rgba(124,58,237,0.06) 0%, rgba(0,0,0,0.3) 100%)',
                borderColor: 'rgba(124,58,237,0.15)',
              }}
              data-testid={`tabletv2-type-${opt.type}`}>
              <div className="absolute top-4 left-6 pointer-events-none">
                <opt.icon className="text-[#7C3AED]/[0.12]" style={{ width: 'clamp(7rem, 16vh, 14rem)', height: 'auto' }} strokeWidth={0.7} />
              </div>
              <div className="relative z-10">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/35 bg-white/8 px-3 py-1 rounded-full inline-block mb-2">
                  {opt.badge}
                </span>
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <h2 className="font-black uppercase leading-[0.95] mb-1" style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(1.8rem, 4vh, 3.2rem)' }}>
                      {opt.title}
                    </h2>
                    <p className="text-sm text-white/40">{opt.desc}</p>
                  </div>
                  <ArrowRight className="w-7 h-7 text-[#7C3AED]/40 flex-shrink-0" />
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        <p className="text-center text-xs uppercase tracking-[0.25em] text-white/25 py-8">
          {t('tabletv2.touchCardToContinue', 'Dotknij wybraną kartę, aby kontynuować')}
        </p>
      </div>
    </motion.div>
  );

  // Step 2: Form + Consents — fits viewport, no scroll
  const stepLabel = accountType === 'family'
    ? t('step2of3', 'Krok 2 z 3 — Twoje dane')
    : t('step2of2', 'Krok 2 z 2 — Twoje dane');
  const formProgress = accountType === 'family' ? 33 : 50;

  const renderFormStep = () => (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: '#050505' }} data-testid="tabletv2-form">
      {renderHeader(() => setStep(1))}
      <div className="px-10 pt-1">
        <ProgressBar percent={formProgress} />
      </div>

      <div className="flex-1 px-10 pt-2 flex flex-col overflow-hidden min-h-0">
        <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#7C3AED] mb-2 flex items-center gap-3">
          <span className="w-10 h-[2px] bg-[#7C3AED]" />
          {stepLabel}
        </p>
        <h1 className="font-black uppercase leading-[0.9] mb-8" style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(1.8rem, 3.5vh, 3rem)' }}>
          <span className="text-white">{t('tabletv2.tellUs', 'Powiedz')}</span>{' '}<br />
          <span className="text-[#7C3AED]">{t('tabletv2.whoYouAre', 'kim jesteś')}</span>
        </h1>

        {error && (
          <div className="mb-2 p-2.5 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
          </div>
        )}

        {/* All fields use vh-based heights via inline style */}
        <div className="flex-1 flex flex-col min-h-0" style={{ gap: 'clamp(0.3rem, 0.8vh, 0.6rem)' }}>
          {/* Row: Name */}
          <div className="grid grid-cols-2 gap-3">
            <Field icon={User} label={t('auth.firstName', 'Imię')}>
              <input type="text" value={formData.firstName}
                onChange={e => handleChange('firstName', e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1))}
                className={inputClass} style={{ height: 'clamp(2.5rem, 5vh, 3.5rem)' }}
                placeholder="Jan" autoComplete="off" autoCapitalize="words" data-testid="tabletv2-firstname" />
            </Field>
            <Field icon={UserCircle} label={t('auth.lastName', 'Nazwisko')}>
              <input type="text" value={formData.lastName}
                onChange={e => handleChange('lastName', e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1))}
                className={inputClass} style={{ height: 'clamp(2.5rem, 5vh, 3.5rem)' }}
                placeholder="Kowalski" autoComplete="off" autoCapitalize="words" data-testid="tabletv2-lastname" />
            </Field>
          </div>

          {/* Email + Phone in one row */}
          <div className="grid grid-cols-2 gap-3">
            <Field icon={Mail} label={t('auth.email', 'E-mail')}>
              <input type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)}
                className={inputClass} style={{ height: 'clamp(2.5rem, 5vh, 3.5rem)' }}
                placeholder="jan@example.com" autoComplete="off" data-testid="tabletv2-email" />
            </Field>
            <Field icon={Phone} label={t('auth.phone', 'Telefon')}>
              <div className="flex gap-2">
                <PhonePrefixSelect value={formData.phoneCode} onChange={(v) => handleChange('phoneCode', v)}
                  className="w-[6.5rem] rounded-xl text-sm" style={{ height: 'clamp(2.5rem, 5vh, 3.5rem)', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                <input type="tel" value={formData.phone}
                  onChange={e => handleChange('phone', e.target.value.replace(/[^0-9\s]/g, ''))}
                  className={inputClass} style={{ height: 'clamp(2.5rem, 5vh, 3.5rem)' }}
                  placeholder="600 000 000" autoComplete="off" inputMode="numeric" data-testid="tabletv2-phone" />
              </div>
            </Field>
          </div>

          {/* Date + Gender */}
          <div className="grid grid-cols-2 gap-3">
            <Field icon={Calendar} label={t('auth.birthDate', 'Data urodzenia')}>
              <input type="date" value={formData.birthDate} onChange={e => handleChange('birthDate', e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                ref={el => { if (el && !el._pickerBound) { el._pickerBound = true; el.addEventListener('click', function () { this.showPicker?.(); }); } }}
                className={inputClass} style={{ height: 'clamp(2.5rem, 5vh, 3.5rem)' }} autoComplete="off" data-testid="tabletv2-birthdate" />
            </Field>
            <Field icon={User} label={t('auth.gender', 'Płeć')}>
              <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
                <SelectTrigger className="w-full rounded-xl text-sm" style={{ height: 'clamp(2.5rem, 5vh, 3.5rem)', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} data-testid="tabletv2-gender">
                  <SelectValue placeholder={t('common.select', 'Wybierz')} />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
                  <SelectItem value="male">{t('auth.male', 'Mężczyzna')}</SelectItem>
                  <SelectItem value="female">{t('auth.female', 'Kobieta')}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/* Password */}
          <Field icon={Lock} label={t('auth.password', 'Hasło')}>
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} value={formData.password}
                onChange={e => handleChange('password', e.target.value)}
                className={`${inputClass} pr-12`} style={{ height: 'clamp(2.5rem, 5vh, 3.5rem)' }}
                placeholder="••••••••" autoComplete="new-password" data-testid="tabletv2-password" />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 p-1">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </Field>

          {formData.birthDate && calculateAge(formData.birthDate) < 18 && <MinorAlert t={t} />}

          {/* Consents divider */}
          <div className="border-t border-white/10 mt-4 pt-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/30 mb-2">{t('auth.consents', 'Zgody i regulaminy')}</p>
          </div>

          {/* Required consents — compact row */}
          <div className="flex flex-col" style={{ gap: 'clamp(0.8rem, 0.4vh, 0.35rem)' }}>
            {[
              { key: 'agreementPrivacyPolicy', slug: 'polityka-prywatnosci', label: t('auth.privacyPolicy', 'Politykę prywatności') },
              { key: 'agreementTermsOfService', slug: 'regulamin-serwisu', label: t('auth.termsOfService', 'Regulamin Serwisu') },
              { key: 'agreementSkateparkRules', slug: 'regulamin-skateparku', label: t('auth.skateparkRules', 'Regulamin Skateparku') },
              { key: 'agreementMarketing', label: t('auth.marketing', 'Zgoda marketingowa'), optional: true },
              { key: 'agreementNewsletter', label: t('auth.newsletterOptional', 'Zapisz mnie do newslettera z nowościami'), optional: true },
            ].map(c => (
              <label key={c.key} className="flex items-center gap-3 px-3 py-3 rounded-xl border border-white/10 bg-white/[0.02] cursor-pointer">
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${formData[c.key] ? 'bg-[#7C3AED] border-[#7C3AED]' : 'border-white/25'}`}>
                  {formData[c.key] && <Check className="w-3 h-3 text-white" />}
                </div>
                <input type="checkbox" className="sr-only" checked={formData[c.key]} onChange={e => handleChange(c.key, e.target.checked)} />
                <span className="text-sm text-white/60">
                  {c.slug ? (
                    <>{t('auth.iAccept', 'Akceptuję ')}<button type="button" onClick={(e) => { e.preventDefault(); openDocumentModal(c.slug, c.label); }} className="text-[#7C3AED] font-semibold underline">{c.label}</button><span className="text-[#7C3AED] ml-0.5">*</span></>
                  ) : (
                    <>{c.label} <span className="text-white/25">{t('auth.optionalConsent', '(opcjonalnie)')}</span></>
                  )}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={handleRegister} disabled={loading}
          className="w-full font-black uppercase tracking-wider rounded-2xl text-white flex items-center justify-center gap-3 bg-[#7C3AED] hover:bg-[#6D28D9] transition-all active:scale-[0.98] disabled:opacity-50 mt-8 mb-8"
          style={{ height: 'clamp(3rem, 5.5vh, 4rem)', fontSize: 'clamp(1rem, 2vh, 1.3rem)' }}
          data-testid="tabletv2-register-btn">
          {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> {t('auth.registering', 'Rejestracja...')}</>
            : <>{t('createAccount', 'Utwórz konto')} <ArrowRight className="w-5 h-5" /></>}
        </button>
      </div>
      {renderWarning()}
      {renderDocumentModal()}
    </div>
  );

  // Step 3: Add person (family only) — same styling as form step
  const renderAddPerson = () => (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: '#050505' }} data-testid="tabletv2-add-person">
      {renderHeader(null)}
      <div className="px-10 pt-1">
        <ProgressBar percent={100} />
      </div>

      <div className="flex-1 px-10 pt-1 pb-4 flex flex-col min-h-0">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#7C3AED] mb-1 flex items-center gap-3">
          <span className="w-8 h-[2px] bg-[#7C3AED]" />
          {t('tabletv2.step3of3', 'Krok 3 z 3 — Dodaj osoby')}
        </p>

        {/* Success banner */}
        <div className="flex items-center gap-3 mb-4 mt-3">
          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <span className="text-sm font-semibold text-white">{t('tablet.accountCreated', 'Konto utworzone!')}</span>
            <span className="text-sm text-white/30 ml-2">{createdUser?.firstName} {createdUser?.lastName}</span>
          </div>
        </div>

        <h1 className="font-black uppercase leading-[0.9] mb-8" style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(1.8rem, 3.5vh, 3rem)' }}>
          <span className="text-white">{t('tabletv2.addYour', 'Dodaj')}{' '}</span><br/>
          <span className="text-[#7C3AED]">{addedSubaccounts.length > 0 ? t('tabletv2.nextPerson', 'kolejną osobę') : t('tabletv2.familyMember', 'bliską osobę')}</span>
        </h1>

        {/* Added people */}
        {addedSubaccounts.length > 0 && (
          <div className="mb-8 flex flex-wrap gap-2">
            {addedSubaccounts.map((sub, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/20 rounded-full text-sm">
                <Check className="w-3.5 h-3.5 text-green-400" />
                <span className="text-green-300 font-medium">{sub.firstName} {sub.lastName}</span>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="mb-2 p-2.5 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
          </div>
        )}

        {/* Fields — same clamp() sizing as form step */}
        <div className="flex-1 flex flex-col min-h-0" style={{ gap: 'clamp(0.3rem, 0.8vh, 0.6rem)' }}>
          <div className="grid grid-cols-2 gap-3">
            <Field icon={User} label={t('auth.firstName', 'Imię')}>
              <input type="text" value={subaccountData.firstName}
                onChange={e => setSubaccountData({ ...subaccountData, firstName: e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1) })}
                className={inputClass} style={{ height: 'clamp(2.5rem, 5vh, 3.5rem)' }}
                placeholder={t('auth.firstName', 'Imię')} autoComplete="off" autoCapitalize="words" />
            </Field>
            <Field icon={UserCircle} label={t('auth.lastName', 'Nazwisko')}>
              <input type="text" value={subaccountData.lastName}
                onChange={e => setSubaccountData({ ...subaccountData, lastName: e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1) })}
                className={inputClass} style={{ height: 'clamp(2.5rem, 5vh, 3.5rem)' }}
                placeholder={t('auth.lastName', 'Nazwisko')} autoComplete="off" autoCapitalize="words" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field icon={Phone} label={t('auth.phone', 'Telefon')}>
              <div className="flex gap-2">
                <PhonePrefixSelect value={subaccountData.phoneCode} onChange={(v) => setSubaccountData({ ...subaccountData, phoneCode: v })}
                  className="w-[6.5rem] rounded-xl text-sm" style={{ height: 'clamp(2.5rem, 5vh, 3.5rem)', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                <input type="tel" value={subaccountData.phone}
                  onChange={e => setSubaccountData({ ...subaccountData, phone: e.target.value.replace(/[^0-9\s]/g, '') })}
                  className={inputClass} style={{ height: 'clamp(2.5rem, 5vh, 3.5rem)' }}
                  placeholder="600 000 000" autoComplete="off" inputMode="numeric" />
              </div>
            </Field>
            <Field icon={Calendar} label={t('auth.birthDate', 'Data urodzenia')}>
              <input type="date" value={subaccountData.birthDate}
                onChange={e => setSubaccountData({ ...subaccountData, birthDate: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                ref={el => { if (el && !el._pickerBound) { el._pickerBound = true; el.addEventListener('click', function () { this.showPicker?.(); }); } }}
                className={inputClass} style={{ height: 'clamp(2.5rem, 5vh, 3.5rem)' }} />
            </Field>
          </div>

          <Field icon={User} label={t('auth.gender', 'Płeć')}>
            <Select value={subaccountData.gender} onValueChange={(v) => setSubaccountData({ ...subaccountData, gender: v })}>
              <SelectTrigger className="w-full rounded-xl text-sm" style={{ height: 'clamp(2.5rem, 5vh, 3.5rem)', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <SelectValue placeholder={t('common.select', 'Wybierz')} />
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
                <SelectItem value="male">{t('auth.male', 'Mężczyzna')}</SelectItem>
                <SelectItem value="female">{t('auth.female', 'Kobieta')}</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          {subaccountData.birthDate && calculateAge(subaccountData.birthDate) < 18 && <MinorAlert t={t} />}
        </div>

        {/* Buttons */}
        <button onClick={handleAddSubaccount} disabled={loading}
          className="w-full font-black uppercase tracking-wider rounded-2xl text-white flex items-center justify-center gap-3 bg-[#7C3AED] hover:bg-[#6D28D9] transition-all active:scale-[0.98] disabled:opacity-50 mt-8 mb-2"
          style={{ height: 'clamp(3rem, 5.5vh, 4rem)', fontSize: 'clamp(1rem, 2vh, 1.3rem)' }}
          data-testid="tabletv2-add-person-btn">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><User className="w-5 h-5" /> {t('tablet.addPerson', 'Dodaj osobę')}</>}
        </button>

        <button onClick={() => goToFinish()}
          className="w-full py-4 text-sm font-semibold uppercase tracking-wider text-white/30 hover:text-white/60 transition-colors"
          data-testid="tabletv2-finish-btn">
          {addedSubaccounts.length > 0 ? t('finishRegistration', 'Zakończ rejestrację') + ' →' : t('skipForNow', 'Pomiń na razie') + ' →'}
        </button>
      </div>
      {renderWarning()}
    </div>
  );

  // Step 4: Print declarations for minors
  // All declarations are sent as ONE print job (one combined HTML document
  // with page-breaks). The printer warms up ONCE, then feeds each page.
  //   - WARMUP: fixed cost for iframe load + Chrome render + OS spool + printer warm-up
  //   - PER_PAGE: incremental cost per subsequent page feed
  const PRINT_WARMUP_MS = 8000;
  const PRINT_PER_PAGE_MS = 5000;
  const printDurationFor = (n) => PRINT_WARMUP_MS + Math.max(0, n) * PRINT_PER_PAGE_MS;

  const handlePrint = () => {
    const minors = getAllMinors();
    const nonSelfMinors = minors.filter(m => !m.isSelf);
    // Only validate presence for non-self minors (family children)
    const allChosen = nonSelfMinors.every(m => {
      const key = `${m.firstName} ${m.lastName}`;
      return presenceChoices[key] === 'without' || presenceChoices[key] === 'with';
    });
    if (!allChosen) {
      setError(t('tablet.print.selectPresence', 'Wybierz opcję obecności dla każdej osoby niepełnoletniej'));
      return;
    }
    setError('');
    setIsPrinting(true);

    const declarations = minors.map(m => ({
      parentFirstName: m.isSelf ? '' : formData.firstName,
      parentLastName: m.isSelf ? '' : formData.lastName,
      childFirstName: m.firstName,
      childLastName: m.lastName,
      childBirthDate: m.birthDate,
      presence: m.isSelf ? '' : (presenceChoices[`${m.firstName} ${m.lastName}`] || ''),
      isSelf: m.isSelf,
    }));

    // Fire-and-forget: kick off the print job IMMEDIATELY so Chrome starts
    // feeding the OS print spool while the overlay animates. DO NOT await —
    // we want the progress bar and the physical print to run in parallel.
    // Using requestAnimationFrame so React has a chance to paint the overlay
    // at least once before the synchronous window.print() call inside the iframe.
    requestAnimationFrame(() => {
      printDeclarations(declarations, lang).catch(err => {
        console.error('Print error:', err);
      });
    });

    // Overlay stays visible for: warm-up (once) + per-page feed time.
    setTimeout(() => {
      setPrintDone(true);
      setIsPrinting(false);
    }, printDurationFor(declarations.length));
  };

  const renderPrintDeclarations = () => {
    const minors = getAllMinors();
    const selfMinors = minors.filter(m => m.isSelf);
    const childMinors = minors.filter(m => !m.isSelf);
    const allSelf = minors.length > 0 && childMinors.length === 0;

    return (
      <div className="h-screen flex flex-col overflow-hidden relative" style={{ background: '#050505' }} data-testid="tabletv2-print">
        {renderHeader(accountType === 'family' ? () => setStep(3) : null)}

        {/* Printing fullscreen overlay */}
        <AnimatePresence>
          {isPrinting && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-[#050505]"
            >
              <div className="text-center">
                <div className="relative w-48 h-48 mx-auto mb-10">
                  <motion.div className="absolute inset-0 rounded-full border-2 border-[#7C3AED]/20"
                    animate={{ scale: [1, 1.5], opacity: [0.4, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }} />
                  <motion.div className="absolute inset-0 rounded-full border border-[#7C3AED]/10"
                    animate={{ scale: [1, 1.8], opacity: [0.3, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut', delay: 0.4 }} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-28 h-28 rounded-full bg-[#7C3AED]/10 flex items-center justify-center">
                      <motion.div animate={{ y: [0, -3, 0] }} transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}>
                        <Printer className="w-14 h-14 text-[#7C3AED]" strokeWidth={1.5} />
                      </motion.div>
                    </div>
                  </div>
                </div>
                <div className="w-48 h-1.5 bg-white/10 rounded-full mx-auto mb-6 overflow-hidden">
                  <motion.div className="h-full bg-[#7C3AED] rounded-full"
                    initial={{ width: '5%' }}
                    animate={{ width: '95%' }}
                    transition={{ duration: printDurationFor(minors.length) / 1000, ease: 'easeInOut' }} />
                </div>
                <h2 className="text-3xl font-black uppercase text-white mb-3" style={{ fontFamily: "'Syne', sans-serif" }}>
                  {t('tablet.print.printingTitle', 'Drukowanie...')}
                </h2>
                <p className="text-lg text-white/40">
                  {t('tablet.print.printingDesc', 'Oświadczenie jest drukowane, proszę czekać')}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex-1 px-10 pt-1 pb-4 flex flex-col min-h-0">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#7C3AED] mb-1 flex items-center gap-3">
            <span className="w-8 h-[2px] bg-[#7C3AED]" />
            {t('tablet.print.stepLabel', 'Oświadczenia')}
          </p>
          <h1 className="font-black uppercase leading-[0.9] mb-2" style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(1.8rem, 3.5vh, 3rem)' }}>
            <span className="text-white">{t('tablet.print.heading1', 'Wydruk')}{' '}</span>
            <span className="text-[#7C3AED]">{t('tablet.print.heading2', 'oświadczeń')}</span>
          </h1>

          {error && (
            <div className="mb-2 p-2.5 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 flex items-center gap-2 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
            </div>
          )}

          {/* Solo minor info banner */}
          {selfMinors.length > 0 && (
            <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-white/70">
                  {t('tablet.print.soloMinorPrintInfo', 'Drukujemy oświadczenie z Twoimi danymi. Poproś rodzica lub opiekuna prawnego o jego wypełnienie i podpisanie.')}
                </p>
              </div>
            </div>
          )}

          {/* Presence question + cards — only for family children (not solo minor) */}
          {childMinors.length > 0 && (
            <>
              <p className="text-base text-white font-medium mb-3 bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3">
                {t('tablet.print.presenceExplainer', 'Czy dziecko będzie korzystać ze skateparku w Twojej obecności, czy samodzielnie?')}
              </p>
              <div className="flex-1 min-h-0 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#7C3AED33 transparent' }}>
                <div className="flex flex-col" style={{ gap: 'clamp(0.4rem, 1vh, 0.8rem)' }}>
                  {childMinors.map((minor) => {
                    const key = `${minor.firstName} ${minor.lastName}`;
                    const choice = presenceChoices[key] || '';
                    return (
                      <div key={key} className="p-4 rounded-2xl border border-white/10 bg-white/[0.03]">
                        <div className="flex items-center gap-3 mb-3">
                          <User className="w-5 h-5 text-[#7C3AED]" />
                          <span className="text-base font-semibold text-white">{minor.firstName} {minor.lastName}</span>
                          <span className="text-xs text-white/30">
                            {minor.birthDate && new Date(minor.birthDate).toLocaleDateString('pl-PL')}
                          </span>
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setPresenceChoices(prev => ({ ...prev, [key]: 'without' }))}
                            className={`flex-1 py-3 px-3 rounded-xl text-center transition-all border-2 ${
                              choice === 'without' ? 'border-[#7C3AED] bg-[#7C3AED]/20 text-white' : 'border-white/10 bg-white/[0.03] text-white/50'
                            }`}
                            data-testid={`presence-without-${minor.firstName}`}>
                            <span className="text-sm font-semibold block">{t('tablet.print.withoutPresence', 'Bez obecności rodzica')}</span>
                            <span className="text-[10px] text-white/30 block mt-0.5">{t('tablet.print.withoutDesc', 'Samodzielnie na skateparku')}</span>
                          </button>
                          <button
                            onClick={() => setPresenceChoices(prev => ({ ...prev, [key]: 'with' }))}
                            className={`flex-1 py-3 px-3 rounded-xl text-center transition-all border-2 ${
                              choice === 'with' ? 'border-[#7C3AED] bg-[#7C3AED]/20 text-white' : 'border-white/10 bg-white/[0.03] text-white/50'
                            }`}
                            data-testid={`presence-with-${minor.firstName}`}>
                            <span className="text-sm font-semibold block">{t('tablet.print.withPresence', 'W obecności rodzica')}</span>
                            <span className="text-[10px] text-white/30 block mt-0.5">{t('tablet.print.withDesc', 'Rodzic towarzyszy na skateparku')}</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Solo minor: spacer when no cards shown */}
          {allSelf && <div className="flex-1" />}

          {/* Buttons */}
          {!printDone ? (
            <button onClick={handlePrint} disabled={isPrinting}
              className="w-full font-black uppercase tracking-wider rounded-2xl text-white flex items-center justify-center gap-3 bg-[#7C3AED] hover:bg-[#6D28D9] transition-all active:scale-[0.98] disabled:opacity-50 mt-3"
              style={{ height: 'clamp(3rem, 5.5vh, 4rem)', fontSize: 'clamp(1rem, 2vh, 1.3rem)' }}
              data-testid="tabletv2-print-btn">
              <Printer className="w-5 h-5" /> {t('tablet.print.printButton', 'Wydrukuj oświadczenia')}
            </button>
          ) : (
            <div className="mt-3">
              <p className="text-center text-sm text-white/50 mb-3">
                {t('tablet.print.didItPrint', 'Czy oświadczenie się wydrukowało?')}
              </p>
              <div className="flex flex-col" style={{ gap: 'clamp(0.4rem, 0.8vh, 0.6rem)' }}>
                <button onClick={() => setStep(5)}
                  className="w-full font-bold uppercase tracking-wider rounded-2xl text-white flex items-center justify-center gap-3 bg-[#7C3AED] hover:bg-[#6D28D9] transition-all active:scale-[0.98]"
                  style={{ height: 'clamp(2.8rem, 5vh, 3.5rem)', fontSize: 'clamp(0.9rem, 1.8vh, 1.2rem)' }}
                  data-testid="tabletv2-confirm-printed">
                  <CheckCircle className="w-5 h-5" /> {t('tablet.print.yesPrinted', 'Tak, wydrukowano')}
                </button>
                <button onClick={() => { setPrintDone(false); handlePrint(); }}
                  className="w-full font-bold uppercase tracking-wider rounded-2xl text-white flex items-center justify-center gap-3 border-2 border-white/15 bg-white/[0.05] hover:bg-white/[0.1] transition-all active:scale-[0.98]"
                  style={{ height: 'clamp(2.8rem, 5vh, 3.5rem)', fontSize: 'clamp(0.9rem, 1.8vh, 1.2rem)' }}
                  data-testid="tabletv2-reprint-btn">
                  <Printer className="w-5 h-5" /> {t('tablet.print.noPrintAgain', 'Nie, wydrukuj ponownie')}
                </button>
                <button onClick={() => setStep(5)}
                  className="w-full py-3 text-xs font-semibold uppercase tracking-wider text-white/25 hover:text-white/50 transition-colors"
                  data-testid="tabletv2-skip-print">
                  {t('tablet.print.skipPrint', 'Pomiń drukowanie')}
                </button>
                <p className="text-center text-[10px] text-white/20 -mt-1">
                  {t('tablet.print.skipNote', 'Oświadczenia znajdziesz również po prawej stronie kiosku rejestracyjnego lub w recepcji.')}
                </p>
              </div>
            </div>
          )}
        </div>
        {renderWarning()}
      </div>
    );
  };

  // Step 5: Thank you — WOW screen with animated checkmark, particles, glow
  // Determine if there are any minors (main user or subaccounts)
  const renderThankYou = () => {
    // Collect minor names
    const minorNames = [];
    // Check main user
    if (formData.birthDate && calculateAge(formData.birthDate) < 18) {
      minorNames.push(formData.firstName);
    }
    // Check subaccounts
    addedSubaccounts.forEach(sub => {
      if (sub.birthDate && calculateAge(sub.birthDate) < 18) {
        minorNames.push(sub.firstName);
      }
    });
    const hasMinors = minorNames.length > 0;

    return (
      <div className="h-screen flex flex-col items-center justify-center p-10 relative overflow-hidden" style={{ background: '#050505' }} data-testid="tabletv2-thank-you">
        {/* Animated floating particles */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 24 }, (_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{
                width: Math.random() * 6 + 3,
                height: Math.random() * 6 + 3,
                background: i % 3 === 0 ? '#7C3AED' : i % 3 === 1 ? '#A78BFA' : '#C4B5FD',
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 0.8, 0],
                scale: [0, 1.5, 0],
                y: [0, -(Math.random() * 300 + 100)],
                x: [(Math.random() - 0.5) * 200],
              }}
              transition={{
                duration: Math.random() * 2 + 1.5,
                delay: Math.random() * 0.8,
                ease: 'easeOut',
              }}
            />
          ))}
        </div>

        {/* Purple glow behind checkmark */}
        <motion.div
          className="absolute rounded-full"
          style={{
            width: 400, height: 400,
            background: 'radial-gradient(circle, rgba(124,58,237,0.3) 0%, rgba(124,58,237,0) 70%)',
            top: '50%', left: '50%', transform: 'translate(-50%, -65%)',
          }}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: [0, 1, 0.7], scale: [0.5, 1.2, 1] }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        />

        {/* Pulsing rings */}
        <motion.div
          className="absolute rounded-full border-2 border-[#7C3AED]/30"
          style={{ width: 200, height: 200, top: '50%', left: '50%', x: '-50%', y: '-65%' }}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: [0, 0.5, 0], scale: [0.8, 2, 2.5] }}
          transition={{ duration: 2, delay: 0.3, ease: 'easeOut' }}
        />
        <motion.div
          className="absolute rounded-full border border-[#7C3AED]/20"
          style={{ width: 200, height: 200, top: '50%', left: '50%', x: '-50%', y: '-65%' }}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: [0, 0.4, 0], scale: [0.8, 2.5, 3] }}
          transition={{ duration: 2.5, delay: 0.5, ease: 'easeOut' }}
        />

        <div className="relative z-10 text-center max-w-xl">
          {/* Animated checkmark circle */}
          <motion.div
            className="w-36 h-36 rounded-full mx-auto mb-8 flex items-center justify-center relative"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
          >
            <motion.div
              className="absolute inset-0 rounded-full bg-[#7C3AED]"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
            />
            <svg className="relative z-10 w-18 h-18" viewBox="0 0 52 52" fill="none">
              <motion.path
                d="M14 27 L22 35 L38 17"
                stroke="white"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.6, delay: 0.5, ease: 'easeInOut' }}
              />
            </svg>
          </motion.div>

          {/* Confetti burst */}
          <div className="absolute left-1/2 top-[7rem] -translate-x-1/2 pointer-events-none">
            {Array.from({ length: 16 }, (_, i) => {
              const angle = (i / 16) * 360;
              const rad = (angle * Math.PI) / 180;
              const distance = 120 + Math.random() * 60;
              return (
                <motion.div
                  key={`confetti-${i}`}
                  className="absolute w-3 h-3 rounded-full"
                  style={{ background: ['#7C3AED', '#A78BFA', '#C4B5FD', '#DDD6FE', '#EDE9FE', '#FFFFFF'][i % 6] }}
                  initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                  animate={{ x: Math.cos(rad) * distance, y: Math.sin(rad) * distance, opacity: 0, scale: 0 }}
                  transition={{ duration: 1, delay: 0.4, ease: 'easeOut' }}
                />
              );
            })}
          </div>

          {/* Heading */}
          <motion.h1
            className="text-5xl font-black uppercase mb-4 text-center"
            style={{ fontFamily: "'Syne', sans-serif" }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <span className="text-[#7C3AED] block mb-8">{t('tablet.success.accountCreated', 'Konto utworzone!')}</span>
          </motion.h1>

          {/* Description — varies based on minors */}
          <motion.div
            className="text-lg text-white/50 mb-10 leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
          >
            {hasMinors ? (
              <>
                <p className="mb-4">
                  {t('tablet.success.completedWithMinors', 'Twoja rejestracja została zakończona. Uzupełnij oświadczenie rodzica / opiekuna prawnego dla')}
                  {' '}<span className="text-white font-semibold">{minorNames.join(', ')}</span>
                  {' '}{t('tablet.success.thenReception', 'a następnie zgłoś się do recepcji.')}
                </p>
                <div className="flex items-center justify-center gap-3 text-amber-400 mt-6">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-base font-semibold text-left">
                    {t('tablet.success.collectPrintedDocs', 'Nie zapomnij odebrać wydrukowanych oświadczeń z drukarki.')}
                  </p>
                </div>
              </>
            ) : (
              <p>{t('tablet.success.completedDefault', 'Twoja rejestracja została zakończona. Możesz teraz zgłosić się do recepcji.')}</p>
            )}
          </motion.div>

          <motion.button
            onClick={resetToWelcome}
            className="px-14 py-6 bg-white/10 hover:bg-white/20 text-white text-xl font-bold rounded-full transition-all uppercase tracking-wider"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3 }}
          >
            {t('tablet.backToStart', 'Powrót do ekranu głównego')}
          </motion.button>

          <motion.p
            className="text-sm text-white/20 mt-10 uppercase tracking-wider"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            {t('tablet.autoReset', 'Ekran automatycznie powróci do strony głównej za 30 sekund')}
          </motion.p>
        </div>
      </div>
    );
  };

  // Maintenance / Loading
  if (serviceStatus === 'checking') {
    return (
      <div className="w-full flex items-center justify-center" style={{ minHeight: '100vh', background: '#050505' }}>
        <div className="absolute top-7 right-7 z-50">
        {renderLangSelector()}
      </div>
        <div className="text-center px-8"><Loader2 className="w-16 h-16 animate-spin text-[#7C3AED] mx-auto mb-6" /><p className="text-xl text-white/40">{t('common.loading', 'Ładowanie...')}</p></div>
      </div>
    );
  }

  if (serviceStatus === 'unavailable') {
    return (
      <div className="w-full flex items-center justify-center relative" style={{ minHeight: '100vh', background: '#050505' }} data-testid="tabletv2-maintenance">
        <div className="absolute top-7 right-7 z-50">
        {renderLangSelector()}
      </div>
        <div className="text-center px-8 max-w-xl">
          <div className="w-24 h-24 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-8"><ServerOff className="w-12 h-12 text-orange-400" /></div>
          <h1 className="text-4xl font-black text-white uppercase mb-4" style={{ fontFamily: "'Syne', sans-serif" }}>{t('maintenance.title', 'Przerwa techniczna')}</h1>
          <div className="w-16 h-1 bg-orange-500 mx-auto mb-6" />
          <p className="text-xl text-white/50 mb-8">{td('maintenance.description') !== 'maintenance.description' ? td('maintenance.description') : t('maintenance.tabletDescription', 'System rejestracji jest chwilowo niedostępny.')}</p>
          <div className="flex items-center justify-center gap-3 text-white/25"><Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm">{t('maintenance.autoRefresh', 'Automatyczne sprawdzanie co 30 sekund...')}</span></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen" style={{ overflow: 'hidden' }}>
      <AnimatePresence mode="wait">
        {step === 0 && <motion.div key="welcome" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.2 } }}>{renderWelcome()}</motion.div>}
        {step === 1 && <motion.div key="type" initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -60 }} transition={{ duration: 0.35 }}>{renderAccountType()}</motion.div>}
        {step === 2 && <motion.div key="form" initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -60 }} transition={{ duration: 0.35 }}>{renderFormStep()}</motion.div>}
        {step === 3 && <motion.div key="add" initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -60 }} transition={{ duration: 0.35 }}>{renderAddPerson()}</motion.div>}
        {step === 4 && <motion.div key="print" initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -60 }} transition={{ duration: 0.35 }}>{renderPrintDeclarations()}</motion.div>}
        {step === 5 && <motion.div key="thanks" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>{renderThankYou()}</motion.div>}
      </AnimatePresence>
    </div>
  );
};

export default TabletRegistrationV2;
